import ArrowForwardIosSharpIcon from "@mui/icons-material/ArrowForwardIosSharp";
import {
  Accordion as MuiAccordion,
  AccordionDetails as MuiAccordionDetails,
  AccordionProps,
  AccordionSummary as MuiAccordionSummary,
  accordionSummaryClasses,
  AccordionSummaryProps,
  styled,
} from "@mui/material";
import { createContext, PropsWithChildren, useContext, useState } from "react";

const StyledAccordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionContext = createContext<
  | {
      expanded: string;
      setExpanded: (expanded: string) => void;
    }
  | undefined
>(undefined);

export function AccordionContextProvider({
  children,
  expanded: _expanded = "",
}: PropsWithChildren<{ expanded?: string }>) {
  const [expanded, setExpanded] = useState<string>(_expanded);
  return (
    <AccordionContext.Provider value={{ expanded, setExpanded }}>
      {children}
    </AccordionContext.Provider>
  );
}

export function useAccordionContext() {
  const context = useContext(AccordionContext);
  return context;
}

export function Accordion({ id, ...props }: AccordionProps) {
  const context = useAccordionContext();
  const [localExpanded, setLocalExpanded] = useState(props.expanded || false);

  const hasContext = context !== undefined;
  const expanded = hasContext ? context.expanded === id : localExpanded;

  const handleChange = (event: React.SyntheticEvent, expanded: boolean) => {
    if (hasContext) {
      context.setExpanded(expanded ? id ?? "" : "");
    } else {
      setLocalExpanded(expanded);
    }

    props.onChange?.(event, expanded);
  };

  return (
    <StyledAccordion
      {...props}
      expanded={expanded}
      onChange={handleChange}
      id={`accordion-${id}`}
      aria-expanded={expanded}
      aria-controls={`accordion-${id}-content`}
      role="region"
      aria-labelledby={`accordion-${id}-header`}
      data-testid={`accordion-${id}`}
    />
  );
}

export const AccordionSummary = styled((props: AccordionSummaryProps) => {
  const { id, ...rest } = props;
  const accordionId = id || "";
  return (
    <MuiAccordionSummary
      expandIcon={<ArrowForwardIosSharpIcon sx={{ fontSize: "0.9rem" }} />}
      id={`accordion-${accordionId}-header`}
      aria-controls={`accordion-${accordionId}-content`}
      {...rest}
    />
  );
})(({ theme }) => ({
  backgroundColor: "rgba(0, 0, 0, .03)",
  flexDirection: "row-reverse",
  [`& .${accordionSummaryClasses.expandIconWrapper}.${accordionSummaryClasses.expanded}`]:
    {
      transform: "rotate(90deg)",
    },
  [`& .${accordionSummaryClasses.content}`]: {
    marginLeft: theme.spacing(1),
  },
  ...theme.applyStyles("dark", {
    backgroundColor: "rgba(255, 255, 255, .05)",
  }),
}));

export const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: "1px solid rgba(0, 0, 0, .125)",
}));
